import React from "react";
import {
  MetadataAuditComparerRun,
  metadataAuditComparerApi,
} from "../../../../../api/metadataAuditComparerApi";
import { ROUTES } from "../../../../../config/routes";
import { WidgetComponentProps } from "../../types";
import { useWidgetData } from "../../useWidgetData";
import {
  CardError,
  CardLoading,
  Freshness,
  Kpi,
  KpiGrid,
  countTone,
  formatNumber,
  formatShortDate,
} from "../../ui";

interface CompositionData {
  openDiscrepancies: number;
  manualReview: number | null;
  blockers: number | null;
  latestRun: MetadataAuditComparerRun | null;
}

/** `total` honours the filter, so a one-row page is enough to count. */
const countLatest = (params: Parameters<typeof metadataAuditComparerApi.listLatestDiscrepancies>[0]) =>
  metadataAuditComparerApi.listLatestDiscrepancies({ ...params, limit: 1 }).then((r) => Number(r.total) || 0);

const CompositionAssetsCard: React.FC<WidgetComponentProps> = () => {
  const { data, loading, error, reload } = useWidgetData<CompositionData>("composition-assets", async () => {
    const [openDiscrepancies, manualReview, blockers, runs] = await Promise.all([
      countLatest({ hasDiscrepancy: true }),
      countLatest({ manualReviewRequired: true }).catch(() => null),
      countLatest({ discrepancySeverity: "blocker" }).catch(() => null),
      metadataAuditComparerApi.listRuns(1).catch(() => ({ runs: [] as MetadataAuditComparerRun[] })),
    ]);
    const latestRun = runs.runs?.[0] ?? null;
    return {
      openDiscrepancies,
      // Fall back to the latest run's counts if the filtered query isn't available.
      manualReview: manualReview ?? latestRun?.rowCounts?.manualReviewRows ?? null,
      blockers,
      latestRun,
    };
  });

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  const run = data.latestRun;
  const link = ROUTES.COMPOSITION_METADATA_AUDIT_COMPARER;

  return (
    <>
      <KpiGrid columns={data.blockers === null ? 2 : 3}>
        <Kpi
          label="Open discrepancies"
          value={data.openDiscrepancies}
          tone={countTone(data.openDiscrepancies)}
          to={link}
          emphasis
        />
        <Kpi
          label="Manual review"
          value={data.manualReview}
          tone={countTone(data.manualReview)}
          hint="Needs a person to check"
          to={link}
        />
        {data.blockers !== null && (
          <Kpi label="Blockers" value={data.blockers} tone={countTone(data.blockers, "bad")} to={link} />
        )}
      </KpiGrid>

      <div className="home-card__meta">
        {run ? (
          <>
            <span className="home-muted">
              Last upload {formatShortDate(run.dateUploaded)}
              {run.rowCounts?.totalRows != null && ` · ${formatNumber(run.rowCounts.totalRows)} rows`}
              {run.status !== "completed" && ` · ${run.status}`}
            </span>
            <Freshness at={run.dateCompleted ?? run.dateUploaded} />
          </>
        ) : (
          <span className="home-muted">No audit runs yet</span>
        )}
      </div>
    </>
  );
};

export default CompositionAssetsCard;
